import type { AxiosRequestConfig } from 'axios';

import type { SwaggerResponse } from '@/services/config';
import { Http } from '@/services/httpRequest';

import type { FeedingDashboardConfig, UpdateFeedingDashboardConfig } from './configTypes';
import type { FeedingDashboardOverviewResponse } from './responseTypes';
import type { FeedingDashboardQuery } from './types';

/**
 * Hand-written fetchers for the v2 feeding-dashboard endpoints.
 * Replace with generated hooks from `@/services` once the OpenAPI pipeline has
 * run against a backend that includes FeedingDashboardController
 * (`yarn workspace optoscale-backend dump:api-swagger` + `yarn workspace optoscale-frontend updateApi`).
 */
const OVERVIEW_URL = (locationId: number) => `/api/v2/feeding-dashboard/${locationId}`;
const CONFIG_URL = (locationId: number) => `/api/v2/feeding-dashboard/${locationId}/config`;
const PEN_CONFIG_URL = (locationId: number, cageId: number) =>
    `/api/v2/feeding-dashboard/${locationId}/pens/${cageId}/config`;

export function getFeedingDashboardOverview(
    locationId: number,
    query?: FeedingDashboardQuery,
    configOverride?: AxiosRequestConfig
): Promise<SwaggerResponse<FeedingDashboardOverviewResponse>> {
    const queryParams = {
        ...(query?.from ? { from: query.from } : {}),
        ...(query?.to ? { to: query.to } : {}),
        ...(query?.cageIds?.length ? { cageIds: query.cageIds.join(',') } : {}),
        // Only when on, so a flag-off request is the same request an old client sends.
        ...(query?.includeFallthrough ? { includeFallthrough: true } : {}),
    };
    return Http.getRequest(
        OVERVIEW_URL(locationId),
        queryParams,
        undefined,
        undefined,
        configOverride
    );
}

export function updateFeedingDashboardConfig(
    locationId: number,
    update: UpdateFeedingDashboardConfig
): Promise<SwaggerResponse<FeedingDashboardConfig>> {
    // The generated axios client (services/config.ts) defaults Content-Type to
    // `application/json-patch+json`, which the backend's JSON body parser does not
    // parse — the body arrives empty and the update silently no-ops. Force a plain
    // JSON content-type so the body is parsed.
    return Http.putRequest(CONFIG_URL(locationId), undefined, update, undefined, {
        headers: { 'Content-Type': 'application/json' },
    });
}

/**
 * Write one pen's threshold overrides. An explicit null clears a field so the pen
 * inherits the site value again; omitting a field leaves any stored override alone.
 * Returns the configuration the pen now resolves to.
 */
export function updateFeedingCageConfig(
    locationId: number,
    cageId: number,
    update: UpdateFeedingDashboardConfig,
    configOverride?: AxiosRequestConfig
): Promise<SwaggerResponse<FeedingDashboardConfig>> {
    return Http.putRequest(
        PEN_CONFIG_URL(locationId, cageId),
        undefined,
        update,
        undefined,
        configOverride
    );
}
